import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/siteData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const showBg = !isHomePage || isScrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showBg
            ? 'bg-white/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className={`font-heading text-xl md:text-2xl font-bold ${showBg ? 'text-foreground-950' : 'text-white'}`}>
              itaobuy<span className="text-primary-500">.living</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${showBg ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
            >
              Home
            </Link>

            <Link
              to="/shopping-guide"
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${showBg ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
            >
              Shopping Guide
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 text-sm font-medium cursor-pointer whitespace-nowrap ${
                  showBg
                    ? 'border-background-300/60 bg-background-50 text-foreground-700 hover:border-foreground-300'
                    : 'border-white/20 text-white hover:border-white/50'
                }`}
              >
                <i className="ri-stack-line w-4 h-4 flex items-center justify-center"></i>
                <span>Categories</span>
                <i className={`w-4 h-4 flex items-center justify-center ${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} transition-transform duration-200`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-[280px] bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-[420px] overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              BUY IT NOW
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-200 cursor-pointer ${
                showBg ? 'bg-foreground-950 text-background-50' : 'bg-white/20 text-white'
              }`}
            >
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-200 cursor-pointer ${
                showBg ? 'text-foreground-950 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`w-5 h-5 flex items-center justify-center ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-16 right-0 w-[300px] max-w-[85vw] h-[calc(100vh-4rem)] bg-background-50 overflow-y-auto border-l border-background-200/70">
            <div className="p-4 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors text-sm font-medium"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Home
              </Link>

              <Link
                to="/shopping-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors text-sm font-medium"
              >
                <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                Shopping Guide
              </Link>

              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors text-sm font-medium cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-stack-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                  Categories
                </span>
                <i className={`w-4 h-4 flex items-center justify-center ${mobileDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {mobileDropdownOpen && (
                <div className="ml-4 border-l-2 border-background-200/70 pl-3 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} w-3.5 h-3.5 flex items-center justify-center text-primary-500`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/70 p-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                BUY IT NOW
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}